export type SortDirection = 'asc' | 'desc'
export type SortSpec<TField extends string> = `${TField},${SortDirection}`

export const SORTABLE_HEADER_CLASS =
  'cursor-pointer select-none whitespace-nowrap py-3 pr-4 text-left text-xl font-semibold text-stone-gray hover:text-dark-blue'

export const ariaSortFor = (isActive: boolean, activeAsc: boolean): 'ascending' | 'descending' | 'none' => {
  if (!isActive) return 'none'
  return activeAsc ? 'ascending' : 'descending'
}

export const parseSort = <TField extends string>(sort: SortSpec<TField>): { field: TField; asc: boolean } => {
  const [field, direction] = sort.split(',') as [TField, SortDirection]
  return { field, asc: direction === 'asc' }
}

export const effectiveSort = <TField extends string>(
  sort: SortSpec<TField> | undefined,
  fallback: SortSpec<TField>
): SortSpec<TField> => sort ?? fallback

// Click cycle: not-active → desc → asc → undefined (= default).
export const nextSort = <TField extends string>(
  col: TField,
  current: SortSpec<TField> | undefined
): SortSpec<TField> | undefined => {
  if (!current) return `${col},desc`
  const { field, asc } = parseSort(current)
  if (field !== col) return `${col},desc`
  if (!asc) return `${col},asc`
  return undefined
}

type SortableHeaderProps<TField extends string> = {
  col: TField
  label: string
  activeField: TField
  activeAsc: boolean
  onSort: (col: TField) => void
}

export const SortableHeader = <TField extends string>({
  col,
  label,
  activeField,
  activeAsc,
  onSort,
}: SortableHeaderProps<TField>): JSX.Element => {
  const isActive = activeField === col
  const arrowClass = isActive ? 'ml-1 text-base' : 'ml-1 text-base text-soft-gray'
  const arrow = isActive && !activeAsc ? '▼' : '▲'
  return (
    <th
      scope="col"
      aria-sort={ariaSortFor(isActive, activeAsc)}
      className={SORTABLE_HEADER_CLASS}
      onClick={() => onSort(col)}
    >
      {label}
      <span className={arrowClass} aria-hidden="true">
        {arrow}
      </span>
    </th>
  )
}
